/**
 * Union-Based Genealogy Layout Engine
 *
 * ARCHITECTURAL FOUNDATION:
 * This engine is built on the genealogically correct model where UNION NODES
 * (couple relationships) are the fundamental building blocks of a family tree.
 *
 * KEY CONCEPTS:
 * 1. UNION NODES - Virtual nodes representing couple relationships
 *    - A union is a married/partner pair at a specific generation
 *    - Children belong TO unions, not to individual parents
 *
 * 2. PERSON NODES - Real people with multiple union participations
 *    - A person can be a CHILD in their parents' union
 *    - A person can be a SPOUSE in their own union(s)
 *    - A person can be a PARENT through their union(s)
 *
 * 3. MULTI-CONTEXT PARTICIPATION
 *    - No "processedPersons" blocking - persons participate in multiple unions
 *    - Same person appears as child, spouse, and parent simultaneously
 *
 * VISUAL STRUCTURE:
 * Generation -1: [Union_B: Father ─── Mother]
 *                         │
 * Generation 0:        [Child] ─── [Spouse] ← Union_A
 *                         │
 * Generation +1:       [GrandChild]
 *
 * ALGORITHM:
 * 1. Determine tree root (most central person)
 * 2. Assign generations via BFS (unlimited depth)
 * 3. Build union nodes from spouse relationships
 * 4. Build person contexts (track union participations)
 * 5. Position unions within each generation
 * 6. Position persons around their unions
 * 7. Generate union-based connections (T-junctions)
 * 8. Center tree and calculate bounds
 */

import type { PersonNodeCanvas, RelationshipEdge } from '@/types/tree-dashboard.types';
import type { Couple } from '@/types/entities';

// ==================== INTERFACES ====================

export interface PositionedNode {
  person: PersonNodeCanvas;
  x: number;
  y: number;
  generation: number;
  branch: string; // 'paternal' | 'maternal' | 'focal' | 'spouse' | 'child' | 'unknown'
}

export interface ConnectionLine {
  id: string;
  type: 'parent-child' | 'spouse';

  // For simple connections (spouse, single parent)
  fromX?: number;
  fromY?: number;
  toX?: number;
  toY?: number;

  // For complex T-junction connections (couples with children)
  pathData?: string; // SVG path "M x,y L x,y ..."
  junctionX?: number; // For junction dot
  junctionY?: number;

  // ==================== PHASE 2 EXTENSIONS ====================
  // Relationship subtype for edge styling (Slice 3 - Adoptive Edge Styling)
  relationship_subtype?: 'biological' | 'adoptive' | 'foster' | null;
}

export interface TreeLayout {
  nodes: PositionedNode[];
  connections: ConnectionLine[];
  unions: UnionNode[]; // Phase 3 Slice 4: Export unions for couple box rendering
  bounds: {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
    width: number;
    height: number;
  };
}

/**
 * Union Node - Represents a couple relationship
 * This is the CORE building block of genealogy trees
 */
export interface UnionNode {
  id: string; // Unique union ID
  spouse1Id: string;
  spouse2Id: string;
  generation: number;
  centerX: number; // Visual center of union (between spouses)
  centerY: number; // Generation Y position
  childIds: string[]; // Children belonging to this union

  // ==================== PHASE 2 EXTENSIONS ====================
  // Couple metadata (Migration 3) - for couple box rendering
  couple_id?: string | null;  // ID from couples table (if explicit couple exists)
  date_start?: string | null;  // Marriage/partnership start date
  date_end?: string | null;  // Marriage/partnership end date
  is_married?: boolean;  // Legal marriage vs cohabitation
  has_ended?: boolean;  // Relationship status (ongoing vs ended)
}

/**
 * Person Context - Tracks a person's participation in unions
 */
interface PersonContext {
  personId: string;
  asChildOf?: string; // Union ID where this person is a child
  asSpouseIn?: string; // Union ID where this person is a spouse
  asParentIn?: string[]; // Union IDs where this person is a parent (same as asSpouseIn)
}

// ==================== CONSTANTS ====================

const NODE_WIDTH = 224; // Person card width (w-56 = 14rem = 224px)
const NODE_HEIGHT = 80; // Approximate person card height

// Genealogy spacing rules (adjusted for MyHeritage-style compact layout)
const GENERATION_GAP = 180; // Vertical spacing between generations (was 250)
const SPOUSE_GAP = 70; // Tight spacing for married couples (unchanged - already good)
const SIBLING_GAP = 80; // Horizontal spacing between siblings at same generation (was 200)
const UNION_SPACING = 150; // Gap between different unions at same generation (was 400)

const PADDING = 100; // Visual breathing room around tree
const T_JUNCTION_DROP = 30; // Vertical drop from parent bar to T-junction point

// ==================== MAIN LAYOUT FUNCTION ====================

/**
 * Compute tree layout using union-based genealogy architecture
 *
 * @param persons - All persons in the tree
 * @param relationships - All relationships (parent_child, spouse)
 * @param focalPersonId - Optional root person (usually tree owner or central person)
 * @param couples - Optional couples data for visual metadata (Phase 3 Slice 4)
 * @param options - Optional layout configuration (Phase 3 Slice 8: depth limits)
 * @returns Complete tree layout with positioned nodes and connections
 */
export function computeTreeLayout(
  persons: PersonNodeCanvas[],
  relationships: RelationshipEdge[],
  focalPersonId?: string,
  couples?: Couple[],
  options?: {
    maxAncestorDepth?: number; // -1 = unlimited (default)
    maxDescendantDepth?: number; // -1 = unlimited (default)
  }
): TreeLayout {
  console.log('[UnionLayout] Input:', {
    persons: persons.length,
    relationships: relationships.length,
    focalPersonId,
  });

  if (persons.length === 0) {
    return emptyLayout();
  }

  try {
    // Step 1: Determine tree root
    const rootId = determineTreeRoot(persons, relationships, focalPersonId);

    console.log('[UnionLayout] Tree root:', {
      rootId,
      rootName: persons.find(p => p.id === rootId)?.first_name,
    });

    // Step 2: Assign generations via BFS traversal (Phase 3 Slice 8: with depth limits)
    const maxAncestorDepth = options?.maxAncestorDepth ?? -1; // -1 = unlimited
    const maxDescendantDepth = options?.maxDescendantDepth ?? -1; // -1 = unlimited

    const generationMap = assignGenerations(
      persons,
      relationships,
      rootId,
      maxAncestorDepth,
      maxDescendantDepth
    );

    console.log('[UnionLayout] Generations assigned:', {
      minGeneration: Math.min(...Array.from(generationMap.values())),
      maxGeneration: Math.max(...Array.from(generationMap.values())),
      totalGenerations: new Set(generationMap.values()).size,
      depthLimits: {
        ancestors: maxAncestorDepth === -1 ? 'unlimited' : maxAncestorDepth,
        descendants: maxDescendantDepth === -1 ? 'unlimited' : maxDescendantDepth,
      },
    });

    // Step 3: Build union nodes (CORE ARCHITECTURAL CHANGE)
    const unions = buildUnionNodes(persons, relationships, generationMap, couples || []);

    console.log('[UnionLayout] Union nodes built:', {
      totalUnions: unions.length,
      unionsByGeneration: countByGeneration(unions),
      couplesWithMetadata: unions.filter(u => u.couple_id).length,
    });

    // Step 4: Build person contexts (track union participations)
    const personContexts = buildPersonContexts(persons, relationships, unions);

    console.log('[UnionLayout] Person contexts built:', {
      totalContexts: personContexts.size,
      personsAsChildren: Array.from(personContexts.values()).filter(c => c.asChildOf).length,
      personsAsSpouses: Array.from(personContexts.values()).filter(c => c.asSpouseIn).length,
    });

    // Step 5: Position unions within each generation
    const positionedUnions = positionUnions(unions, generationMap, personContexts);

    console.log('[UnionLayout] Unions positioned:', {
      positionedUnions: positionedUnions.length,
    });

    // Step 6: Position persons around their unions
    const positionedPersons = positionPersonsAroundUnions(
      persons,
      personContexts,
      positionedUnions,
      generationMap,
      relationships,
      rootId
    );

    console.log('[UnionLayout] Persons positioned:', {
      positionedPersons: positionedPersons.length,
    });

    // Step 7: Calculate centering offset BEFORE generating connections
    const centerOffset = calculateCenterOffset(positionedPersons);

    // Step 8: Apply the same offset to both nodes and unions
    const centeredNodes = positionedPersons.map(node => ({
      ...node,
      x: node.x + centerOffset,
    }));

    const centeredUnions = positionedUnions.map(union => ({
      ...union,
      centerX: union.centerX + centerOffset,
    }));

    // Step 9: Generate union-based connections from CENTERED positions
    const connections = generateUnionBasedConnections(
      centeredNodes,
      centeredUnions,
      relationships
    );

    console.log('[UnionLayout] Connections generated:', {
      connections: connections.length,
      parentChild: connections.filter(c => c.type === 'parent-child').length,
      spouse: connections.filter(c => c.type === 'spouse').length,
    });

    // Step 10: Calculate bounds from centered nodes
    const bounds = calculateBounds(centeredNodes);

    console.log('[UnionLayout] Layout complete:', {
      bounds,
      totalNodes: centeredNodes.length,
      totalConnections: connections.length,
      totalUnions: centeredUnions.length,
    });

    return { nodes: centeredNodes, connections, unions: centeredUnions, bounds };
  } catch (error) {
    console.error('[UnionLayout] Layout computation failed:', error);
    return emptyLayout();
  }
}

// ==================== GENERATION ASSIGNMENT ====================

/**
 * Assign generation numbers via BFS from root person
 * Generation 0 = focal/root person
 * Negative generations = ancestors (parents, grandparents, etc.)
 * Positive generations = descendants (children, grandchildren, etc.)
 *
 * Phase 3 Slice 8: Enhanced with depth limits for controlled tree size
 *
 * @param persons - All persons in the tree
 * @param relationships - All relationships
 * @param rootId - Root person for generation assignment
 * @param maxAncestorDepth - Maximum generations upward (-1 = unlimited)
 * @param maxDescendantDepth - Maximum generations downward (-1 = unlimited)
 * @returns Map of personId -> generation number
 */
function assignGenerations(
  persons: PersonNodeCanvas[],
  relationships: RelationshipEdge[],
  rootId: string | null,
  maxAncestorDepth: number = -1,
  maxDescendantDepth: number = -1
): Map<string, number> {
  const generationMap = new Map<string, number>();

  if (!rootId) {
    console.warn('[UnionLayout] No root person found, cannot assign generations');
    return generationMap;
  }

  // Build adjacency maps for efficient traversal
  const childrenMap = new Map<string, string[]>(); // parent → children[]
  const parentsMap = new Map<string, string[]>(); // child → parents[]
  const spousesMap = new Map<string, string[]>(); // person → spouses[]

  relationships.forEach(rel => {
    if (rel.relationship_type === 'parent_child') {
      const parentId = rel.person_a_id;
      const childId = rel.person_b_id;

      // Parent → Children
      if (!childrenMap.has(parentId)) {
        childrenMap.set(parentId, []);
      }
      childrenMap.get(parentId)!.push(childId);

      // Child → Parents
      if (!parentsMap.has(childId)) {
        parentsMap.set(childId, []);
      }
      parentsMap.get(childId)!.push(parentId);
    } else if (rel.relationship_type === 'spouse') {
      // Bidirectional spouse mapping
      if (!spousesMap.has(rel.person_a_id)) {
        spousesMap.set(rel.person_a_id, []);
      }
      spousesMap.get(rel.person_a_id)!.push(rel.person_b_id);

      if (!spousesMap.has(rel.person_b_id)) {
        spousesMap.set(rel.person_b_id, []);
      }
      spousesMap.get(rel.person_b_id)!.push(rel.person_a_id);
    }
  });

  // BFS to assign generations (Phase 3 Slice 8: with depth limits)
  const visited = new Set<string>();
  const queue: Array<{ personId: string; generation: number }> = [
    { personId: rootId, generation: 0 },
  ];

  while (queue.length > 0) {
    const { personId, generation } = queue.shift()!;

    if (visited.has(personId)) continue;
    visited.add(personId);

    // Phase 3 Slice 8: Check depth limits
    // Skip persons beyond depth boundaries
    const exceedsAncestorLimit = maxAncestorDepth !== -1 && generation < -maxAncestorDepth;
    const exceedsDescendantLimit = maxDescendantDepth !== -1 && generation > maxDescendantDepth;

    if (exceedsAncestorLimit || exceedsDescendantLimit) {
      // Person is beyond depth limit - skip (don't add to generationMap or queue descendants)
      continue;
    }

    // Assign generation (use most extreme value if multiple paths exist)
    const existingGen = generationMap.get(personId);
    if (existingGen === undefined) {
      generationMap.set(personId, generation);
    } else {
      // For ancestors (negative), use more negative value
      // For descendants (positive), use more positive value
      if (generation < 0 && generation < existingGen) {
        generationMap.set(personId, generation);
      } else if (generation > 0 && generation > existingGen) {
        generationMap.set(personId, generation);
      }
    }

    // Process parents (generation - 1)
    // Phase 3 Slice 8: Stop traversal if next generation would exceed ancestor limit
    const nextAncestorGeneration = generation - 1;
    const wouldExceedAncestorLimit = maxAncestorDepth !== -1 && nextAncestorGeneration < -maxAncestorDepth;

    if (!wouldExceedAncestorLimit) {
      const parents = parentsMap.get(personId) || [];
      parents.forEach(parentId => {
        if (!visited.has(parentId)) {
          queue.push({ personId: parentId, generation: nextAncestorGeneration });
        }
      });
    }

    // Process children (generation + 1)
    // Phase 3 Slice 8: Stop traversal if next generation would exceed descendant limit
    const nextDescendantGeneration = generation + 1;
    const wouldExceedDescendantLimit = maxDescendantDepth !== -1 && nextDescendantGeneration > maxDescendantDepth;

    if (!wouldExceedDescendantLimit) {
      const children = childrenMap.get(personId) || [];
      children.forEach(childId => {
        if (!visited.has(childId)) {
          queue.push({ personId: childId, generation: nextDescendantGeneration });
        }
      });
    }

    // Process spouses (same generation)
    const spouses = spousesMap.get(personId) || [];
    spouses.forEach(spouseId => {
      if (!visited.has(spouseId)) {
        queue.push({ personId: spouseId, generation });
      }
    });
  }

  return generationMap;
}

// ==================== UNION NODE BUILDING ====================

/**
 * Build union nodes from spouse relationships
 * Each union represents a couple and owns their children
 *
 * Phase 3 Slice 4: Populates couple metadata (couple_id, date_start, date_end, is_married, has_ended)
 */
function buildUnionNodes(
  persons: PersonNodeCanvas[],
  relationships: RelationshipEdge[],
  generationMap: Map<string, number>,
  couples: Couple[]
): UnionNode[] {
  const unions: UnionNode[] = [];

  // Find all spouse relationships
  const spouseRelationships = relationships.filter(
    rel => rel.relationship_type === 'spouse'
  );

  spouseRelationships.forEach(rel => {
    const spouse1 = persons.find(p => p.id === rel.person_a_id);
    const spouse2 = persons.find(p => p.id === rel.person_b_id);

    if (!spouse1 || !spouse2) return;

    const generation = generationMap.get(spouse1.id) ?? 0;

    // Phase 3 Slice 4: Find matching couple record for metadata
    const couple = couples.find(c =>
      (c.person1_id === rel.person_a_id && c.person2_id === rel.person_b_id) ||
      (c.person1_id === rel.person_b_id && c.person2_id === rel.person_a_id)
    );

    // Phase 3 Slice 5: Find children of this union using couple_id when available
    const childIds = findChildrenOfUnion(
      rel.person_a_id,
      rel.person_b_id,
      relationships,
      couple?.id || null,  // Pass couple_id to enable Phase 2 child detection
      persons               // Pass persons array to check parents_id field
    );

    unions.push({
      id: `union-${rel.id}`,
      spouse1Id: rel.person_a_id,
      spouse2Id: rel.person_b_id,
      generation,
      centerX: 0, // Will be calculated during positioning
      centerY: generation * GENERATION_GAP,
      childIds,
      // Phase 3 Slice 4: Couple metadata for visual rendering
      couple_id: couple?.id || null,
      date_start: couple?.date_start || null,
      date_end: couple?.date_end || null,
      is_married: couple?.is_married || false,
      has_ended: couple?.has_ended || false,
    });
  });

  // Detect implicit unions (co-parents without explicit spouse relationship)
  const parentPairs = new Map<string, Set<string>>(); // child → [parent1, parent2]

  relationships.forEach(rel => {
    if (rel.relationship_type === 'parent_child') {
      const childId = rel.person_b_id;
      const parentId = rel.person_a_id;

      if (!parentPairs.has(childId)) {
        parentPairs.set(childId, new Set());
      }
      parentPairs.get(childId)!.add(parentId);
    }
  });

  // Create implicit unions for parent pairs
  parentPairs.forEach((parents, childId) => {
    if (parents.size === 2) {
      const parentsArray = Array.from(parents);
      const parent1 = parentsArray[0];
      const parent2 = parentsArray[1];

      // Type guard: ensure both parents exist
      if (!parent1 || !parent2) return;

      // Check if explicit union already exists
      const existingUnion = unions.find(
        u =>
          (u.spouse1Id === parent1 && u.spouse2Id === parent2) ||
          (u.spouse1Id === parent2 && u.spouse2Id === parent1)
      );

      if (!existingUnion) {
        // Create implicit union
        const generation = generationMap.get(parent1) ?? 0;

        // Phase 3 Slice 4: Check for couple metadata for implicit unions too
        const implicitCouple = couples.find(c =>
          (c.person1_id === parent1 && c.person2_id === parent2) ||
          (c.person1_id === parent2 && c.person2_id === parent1)
        );

        // Phase 3 Slice 5: Find children using couple_id when available
        const childIds = findChildrenOfUnion(
          parent1,
          parent2,
          relationships,
          implicitCouple?.id || null,  // Pass couple_id to enable Phase 2 child detection
          persons                       // Pass persons array to check parents_id field
        );

        unions.push({
          id: `implicit-union-${parent1}-${parent2}`,
          spouse1Id: parent1,
          spouse2Id: parent2,
          generation,
          centerX: 0,
          centerY: generation * GENERATION_GAP,
          childIds,
          // Phase 3 Slice 4: Couple metadata (may be null for implicit unions)
          couple_id: implicitCouple?.id || null,
          date_start: implicitCouple?.date_start || null,
          date_end: implicitCouple?.date_end || null,
          is_married: implicitCouple?.is_married || false,
          has_ended: implicitCouple?.has_ended || false,
        });
      }
    }
  });

  return unions;
}

/**
 * Find children who belong to a specific couple/union
 *
 * Phase 3 Slice 5: Enhanced to respect couples table data
 *
 * Strategy:
 * 1. If couple_id exists: Find children via persons.parents_id (adoptive) + father_id/mother_id (biological)
 * 2. Fallback: Use relationships table (legacy parent_child edges)
 *
 * This ensures children are correctly grouped by couple, supporting:
 * - Adoptive children (persons.parents_id = couple.id)
 * - Biological children (persons.father_id/mother_id match parents)
 * - Mixed families (biological + adoptive children from same couple)
 * - Remarriage scenarios (children grouped by which couple produced them)
 *
 * @param parent1Id - First parent ID
 * @param parent2Id - Second parent ID
 * @param relationships - All relationships (fallback data source)
 * @param coupleId - Optional couple ID from couples table (Phase 2)
 * @param persons - All persons (needed to check parents_id field)
 * @returns Array of child IDs belonging to this union
 */
function findChildrenOfUnion(
  parent1Id: string,
  parent2Id: string,
  relationships: RelationshipEdge[],
  coupleId?: string | null,
  persons?: PersonNodeCanvas[]
): string[] {
  // Strategy 1: If couple_id exists, use Phase 2 parentage fields
  if (coupleId && persons) {
    const allChildren = new Set<string>();

    // Find adoptive children (persons.parents_id = couple.id)
    const adoptiveChildren = persons.filter(p => p.parents_id === coupleId);
    adoptiveChildren.forEach(child => allChildren.add(child.id));

    // Find biological children (father_id/mother_id match this couple's parents)
    const biologicalChildren = persons.filter(p => {
      const hasFatherMatch = p.father_id === parent1Id || p.father_id === parent2Id;
      const hasMotherMatch = p.mother_id === parent1Id || p.mother_id === parent2Id;
      // Child must have BOTH parents matching this couple
      return hasFatherMatch && hasMotherMatch;
    });
    biologicalChildren.forEach(child => allChildren.add(child.id));

    // If we found children via Phase 2 data, return them
    if (allChildren.size > 0) {
      return Array.from(allChildren);
    }
  }

  // Strategy 2: Fallback to relationships table (legacy/existing logic)
  // This handles cases where:
  // - No couples record exists (implicit unions)
  // - Phase 2 parentage fields not populated
  // - Backward compatibility with existing trees
  const parent1Children = relationships
    .filter(rel => rel.relationship_type === 'parent_child' && rel.person_a_id === parent1Id)
    .map(rel => rel.person_b_id);

  const parent2Children = relationships
    .filter(rel => rel.relationship_type === 'parent_child' && rel.person_a_id === parent2Id)
    .map(rel => rel.person_b_id);

  // Intersection: children with both parents
  return parent1Children.filter(childId => parent2Children.includes(childId));
}

// ==================== PERSON CONTEXT BUILDING ====================

/**
 * Build person contexts to track union participations
 * A person can participate in multiple unions simultaneously
 */
function buildPersonContexts(
  persons: PersonNodeCanvas[],
  relationships: RelationshipEdge[],
  unions: UnionNode[]
): Map<string, PersonContext> {
  const contexts = new Map<string, PersonContext>();

  // Initialize contexts for all persons
  persons.forEach(person => {
    contexts.set(person.id, {
      personId: person.id,
      asParentIn: [],
    });
  });

  // Track union participations
  unions.forEach(union => {
    // Mark spouses
    const spouse1Context = contexts.get(union.spouse1Id);
    const spouse2Context = contexts.get(union.spouse2Id);

    if (spouse1Context) {
      spouse1Context.asSpouseIn = union.id;
      spouse1Context.asParentIn?.push(union.id);
    }

    if (spouse2Context) {
      spouse2Context.asSpouseIn = union.id;
      spouse2Context.asParentIn?.push(union.id);
    }

    // Mark children
    union.childIds.forEach(childId => {
      const childContext = contexts.get(childId);
      if (childContext) {
        childContext.asChildOf = union.id;
      }
    });
  });

  return contexts;
}

// ==================== SUBTREE WIDTH CALCULATION ====================

/**
 * Calculate the width required for a union and all its descendants
 * This is critical for proper tree centering and collision avoidance
 */
interface SubtreeMetrics {
  width: number;
  leftExtent: number;  // Distance from union center to leftmost descendant
  rightExtent: number; // Distance from union center to rightmost descendant
}

/**
 * Calculate subtree width for each union recursively
 * This enables proper parent-over-children centering
 */
function calculateSubtreeWidths(
  unions: UnionNode[],
  personContexts: Map<string, PersonContext>
): Map<string, SubtreeMetrics> {
  const subtreeMetrics = new Map<string, SubtreeMetrics>();
  const unionMap = new Map<string, UnionNode>();
  const childUnionsMap = new Map<string, UnionNode[]>();

  // Build union lookup and parent-child union relationships
  unions.forEach(union => {
    unionMap.set(union.id, union);

    // Find child unions (unions where spouses are children of this union)
    union.childIds.forEach(childId => {
      const childContext = personContexts.get(childId);
      if (childContext?.asSpouseIn) {
        const childUnion = unions.find(u => u.id === childContext.asSpouseIn);
        if (childUnion) {
          if (!childUnionsMap.has(union.id)) {
            childUnionsMap.set(union.id, []);
          }
          childUnionsMap.get(union.id)!.push(childUnion);
        }
      }
    });
  });

  // Calculate metrics recursively (bottom-up)
  function calculateMetrics(unionId: string): SubtreeMetrics {
    if (subtreeMetrics.has(unionId)) {
      return subtreeMetrics.get(unionId)!;
    }

    const union = unionMap.get(unionId);
    if (!union) {
      return { width: NODE_WIDTH * 2 + SPOUSE_GAP, leftExtent: 0, rightExtent: 0 };
    }

    const childUnions = childUnionsMap.get(unionId) || [];
    const coupleWidth = NODE_WIDTH * 2 + SPOUSE_GAP;

    // Base case: no child unions
    if (childUnions.length === 0) {
      // Consider direct children who aren't in unions
      const singleChildren = union.childIds.filter(childId => {
        const context = personContexts.get(childId);
        return !context?.asSpouseIn;
      });

      if (singleChildren.length === 0) {
        const metrics = {
          width: coupleWidth,
          leftExtent: -coupleWidth / 2,
          rightExtent: coupleWidth / 2
        };
        subtreeMetrics.set(unionId, metrics);
        return metrics;
      }

      // Width needed for single children
      const childrenWidth = singleChildren.length * NODE_WIDTH +
                           (singleChildren.length - 1) * SIBLING_GAP;
      const totalWidth = Math.max(coupleWidth, childrenWidth);

      const metrics = {
        width: totalWidth,
        leftExtent: -totalWidth / 2,
        rightExtent: totalWidth / 2
      };
      subtreeMetrics.set(unionId, metrics);
      return metrics;
    }

    // Recursive case: has child unions
    let totalChildWidth = 0;
    let leftmostExtent = 0;
    let rightmostExtent = 0;

    childUnions.forEach((childUnion, index) => {
      const childMetrics = calculateMetrics(childUnion.id);
      if (index === 0) {
        leftmostExtent = -childMetrics.width / 2;
      }
      totalChildWidth += childMetrics.width;
      if (index < childUnions.length - 1) {
        totalChildWidth += UNION_SPACING;
      }
      if (index === childUnions.length - 1) {
        rightmostExtent = totalChildWidth - childMetrics.width / 2;
      }
    });

    // Include single children in width calculation
    const singleChildren = union.childIds.filter(childId => {
      const context = personContexts.get(childId);
      return !context?.asSpouseIn;
    });

    if (singleChildren.length > 0) {
      const singleChildrenWidth = singleChildren.length * NODE_WIDTH +
                                  (singleChildren.length - 1) * SIBLING_GAP;
      totalChildWidth = Math.max(totalChildWidth, singleChildrenWidth);
    }

    const finalWidth = Math.max(coupleWidth, totalChildWidth);
    const metrics = {
      width: finalWidth,
      leftExtent: Math.min(-coupleWidth / 2, leftmostExtent),
      rightExtent: Math.max(coupleWidth / 2, rightmostExtent)
    };

    subtreeMetrics.set(unionId, metrics);
    return metrics;
  }

  // Calculate metrics for all root unions (those without parents)
  unions.forEach(union => {
    calculateMetrics(union.id);
  });

  return subtreeMetrics;
}

// ==================== UNION POSITIONING ====================

/**
 * Position unions using subtree-aware layout
 * This creates a properly centered, branch-aware tree structure
 */
function positionUnions(
  unions: UnionNode[],
  generationMap: Map<string, number>,
  personContexts: Map<string, PersonContext>
): UnionNode[] {
  // Calculate subtree widths first
  const subtreeMetrics = calculateSubtreeWidths(unions, personContexts);

  // Build parent-child union relationships
  const unionParentMap = new Map<string, string>(); // child union -> parent union
  const unionChildrenMap = new Map<string, string[]>(); // parent union -> child unions

  unions.forEach(parentUnion => {
    parentUnion.childIds.forEach(childId => {
      const childContext = personContexts.get(childId);
      if (childContext?.asSpouseIn) {
        const childUnion = unions.find(u => u.id === childContext.asSpouseIn);
        if (childUnion) {
          unionParentMap.set(childUnion.id, parentUnion.id);
          if (!unionChildrenMap.has(parentUnion.id)) {
            unionChildrenMap.set(parentUnion.id, []);
          }
          unionChildrenMap.get(parentUnion.id)!.push(childUnion.id);
        }
      }
    });
  });

  // Position unions recursively from root
  const positionedUnions = new Map<string, UnionNode>();
  const visitedUnions = new Set<string>();

  // Find root unions (those without parents)
  const rootUnions = unions.filter(u => !unionParentMap.has(u.id));

  // Group root unions by generation for initial positioning
  const rootsByGeneration = new Map<number, UnionNode[]>();
  rootUnions.forEach(union => {
    if (!rootsByGeneration.has(union.generation)) {
      rootsByGeneration.set(union.generation, []);
    }
    rootsByGeneration.get(union.generation)!.push(union);
  });

  // Position root unions in each generation
  rootsByGeneration.forEach((genRoots, generation) => {
    let currentX = 0;

    // Calculate total width needed
    let totalWidth = 0;
    genRoots.forEach((union, index) => {
      const metrics = subtreeMetrics.get(union.id) || { width: NODE_WIDTH * 2 + SPOUSE_GAP, leftExtent: 0, rightExtent: 0 };
      totalWidth += metrics.width;
      if (index < genRoots.length - 1) {
        totalWidth += UNION_SPACING;
      }
    });

    // Center the generation
    currentX = -totalWidth / 2;

    genRoots.forEach(union => {
      const metrics = subtreeMetrics.get(union.id) || { width: NODE_WIDTH * 2 + SPOUSE_GAP, leftExtent: 0, rightExtent: 0 };

      const positioned: UnionNode = {
        ...union,
        centerX: currentX + metrics.width / 2,
        centerY: generation * GENERATION_GAP
      };

      positionedUnions.set(union.id, positioned);
      visitedUnions.add(union.id);

      // Position child unions recursively
      positionChildUnions(union.id, positioned.centerX);

      currentX += metrics.width + UNION_SPACING;
    });
  });

  // Recursive function to position child unions centered under parents
  function positionChildUnions(parentUnionId: string, parentCenterX: number) {
    const childUnionIds = unionChildrenMap.get(parentUnionId) || [];
    if (childUnionIds.length === 0) return;

    let totalChildWidth = 0;
    const childMetricsList: SubtreeMetrics[] = [];

    childUnionIds.forEach((childId, index) => {
      const metrics = subtreeMetrics.get(childId) || { width: NODE_WIDTH * 2 + SPOUSE_GAP, leftExtent: 0, rightExtent: 0 };
      childMetricsList.push(metrics);
      totalChildWidth += metrics.width;
      if (index < childUnionIds.length - 1) {
        totalChildWidth += UNION_SPACING;
      }
    });

    // Center children under parent
    let currentChildX = parentCenterX - totalChildWidth / 2;

    childUnionIds.forEach((childId, index) => {
      const childUnion = unions.find(u => u.id === childId);
      if (!childUnion || visitedUnions.has(childId)) return;

      const metrics = childMetricsList[index];
      if (!metrics) return; // Type guard for TypeScript

      const positioned: UnionNode = {
        ...childUnion,
        centerX: currentChildX + metrics.width / 2,
        centerY: childUnion.generation * GENERATION_GAP
      };

      positionedUnions.set(childId, positioned);
      visitedUnions.add(childId);

      // Recursively position this union's children
      positionChildUnions(childId, positioned.centerX);

      currentChildX += metrics.width + UNION_SPACING;
    });
  }

  // Handle any remaining unions not connected to roots (orphans)
  unions.forEach(union => {
    if (!visitedUnions.has(union.id)) {
      const positioned: UnionNode = {
        ...union,
        centerX: 0,
        centerY: union.generation * GENERATION_GAP
      };
      positionedUnions.set(union.id, positioned);
    }
  });

  return Array.from(positionedUnions.values());
}

// ==================== PERSON POSITIONING ====================

/**
 * Position persons around their unions with coherent sibling grouping
 * - Spouses positioned relative to union center
 * - Sibling groups positioned as coherent units
 * - Singles positioned with branch awareness
 */
function positionPersonsAroundUnions(
  persons: PersonNodeCanvas[],
  personContexts: Map<string, PersonContext>,
  unions: UnionNode[],
  generationMap: Map<string, number>,
  relationships: RelationshipEdge[],
  rootId: string | null
): PositionedNode[] {
  const nodes: PositionedNode[] = [];
  const unionMap = new Map<string, UnionNode>();
  const processedPersons = new Set<string>();

  unions.forEach(union => unionMap.set(union.id, union));

  // Group persons by their role and generation for coherent positioning
  const generationGroups = new Map<number, {
    spouses: PersonNodeCanvas[],
    children: Map<string, PersonNodeCanvas[]>, // unionId -> children
    singles: PersonNodeCanvas[]
  }>();

  // Categorize all persons
  persons.forEach(person => {
    const context = personContexts.get(person.id);
    const generation = generationMap.get(person.id) ?? 0;

    if (!generationGroups.has(generation)) {
      generationGroups.set(generation, {
        spouses: [],
        children: new Map(),
        singles: []
      });
    }

    const group = generationGroups.get(generation)!;

    if (context?.asSpouseIn) {
      group.spouses.push(person);
    } else if (context?.asChildOf) {
      if (!group.children.has(context.asChildOf)) {
        group.children.set(context.asChildOf, []);
      }
      group.children.get(context.asChildOf)!.push(person);
    } else {
      group.singles.push(person);
    }
  });

  // Position spouses first (they're anchored to unions)
  generationGroups.forEach((group, generation) => {
    const yPosition = generation * GENERATION_GAP;

    group.spouses.forEach(person => {
      const context = personContexts.get(person.id);
      if (!context?.asSpouseIn) return;

      const union = unionMap.get(context.asSpouseIn);
      if (!union) return;

      const person1 = persons.find(p => p.id === union.spouse1Id);
      const person2 = persons.find(p => p.id === union.spouse2Id);

      const context1 = personContexts.get(union.spouse1Id);
      const context2 = personContexts.get(union.spouse2Id);

      // CRITICAL FIX: Bloodline vs Married-in Distinction
      // A bloodline member has an asChildOf context (they are a child in this tree)
      // A married-in spouse only has asSpouseIn context
      const spouse1IsBloodline = context1?.asChildOf !== undefined;
      const spouse2IsBloodline = context2?.asChildOf !== undefined;

      let leftSpouseId: string;

      if (spouse1IsBloodline && !spouse2IsBloodline) {
        // Spouse 1 is bloodline, keep them in sibling flow (left for LTR)
        leftSpouseId = union.spouse1Id;
      } else if (!spouse1IsBloodline && spouse2IsBloodline) {
        // Spouse 2 is bloodline, keep them in sibling flow (left for LTR)
        leftSpouseId = union.spouse2Id;
      } else {
        // Both are bloodline or both are married-in, fall back to gender-based positioning
        leftSpouseId = person1?.gender === 'male' ||
                      (person1?.gender !== 'female' && person.id === union.spouse1Id)
                      ? union.spouse1Id
                      : union.spouse2Id;
      }

      const isLeftSpouse = person.id === leftSpouseId;

      const halfGap = SPOUSE_GAP / 2;
      const xPosition = isLeftSpouse
        ? union.centerX - NODE_WIDTH - halfGap
        : union.centerX + halfGap;

      nodes.push({
        person,
        x: xPosition,
        y: yPosition,
        generation,
        branch: determineBranch(person, relationships, rootId),
      });

      processedPersons.add(person.id);
    });
  });

  // Position sibling groups as coherent units
  generationGroups.forEach((group, generation) => {
    const yPosition = generation * GENERATION_GAP;

    group.children.forEach((siblings, unionId) => {
      const union = unionMap.get(unionId);
      if (!union) return;

      // Sort siblings for consistent ordering (by age if available, otherwise by name)
      siblings.sort((a, b) => {
        if (a.date_of_birth && b.date_of_birth) {
          return new Date(a.date_of_birth).getTime() - new Date(b.date_of_birth).getTime();
        }
        return (a.first_name || '').localeCompare(b.first_name || '');
      });

      // Calculate sibling group dimensions
      const siblingCount = siblings.length;
      const groupWidth = siblingCount * NODE_WIDTH + (siblingCount - 1) * SIBLING_GAP;

      // Center group under parent union
      const groupCenterX = union.centerX;
      const startX = groupCenterX - groupWidth / 2;

      // Position each sibling in the group
      siblings.forEach((sibling, index) => {
        const xPosition = startX + index * (NODE_WIDTH + SIBLING_GAP);

        nodes.push({
          person: sibling,
          x: xPosition,
          y: yPosition,
          generation,
          branch: determineBranch(sibling, relationships, rootId),
        });

        processedPersons.add(sibling.id);
      });
    });
  });

  // Position singles with branch awareness
  const singlesPerGeneration = new Map<number, PersonNodeCanvas[]>();

  generationGroups.forEach((group, generation) => {
    const unprocessedSingles = group.singles.filter(p => !processedPersons.has(p.id));
    if (unprocessedSingles.length > 0) {
      singlesPerGeneration.set(generation, unprocessedSingles);
    }
  });

  // Find the rightmost position per generation to avoid overlap
  const rightmostPerGeneration = new Map<number, number>();
  nodes.forEach(node => {
    const rightEdge = node.x + NODE_WIDTH;
    const currentRightmost = rightmostPerGeneration.get(node.generation) ?? -Infinity;
    rightmostPerGeneration.set(node.generation, Math.max(currentRightmost, rightEdge));
  });

  // Position singles to the right of their generation
  singlesPerGeneration.forEach((singles, generation) => {
    const yPosition = generation * GENERATION_GAP;
    const startX = (rightmostPerGeneration.get(generation) ?? 0) + UNION_SPACING;

    singles.forEach((person, index) => {
      const xPosition = startX + index * (NODE_WIDTH + SIBLING_GAP);

      nodes.push({
        person,
        x: xPosition,
        y: yPosition,
        generation,
        branch: determineBranch(person, relationships, rootId),
      });

      processedPersons.add(person.id);
    });
  });

  // Final pass: Apply branch-aware collision resolution
  const generationNodes = new Map<number, PositionedNode[]>();
  nodes.forEach(node => {
    if (!generationNodes.has(node.generation)) {
      generationNodes.set(node.generation, []);
    }
    generationNodes.get(node.generation)!.push(node);
  });

  // Sort nodes in each generation by X position and resolve collisions
  generationNodes.forEach((genNodes, generation) => {
    genNodes.sort((a, b) => a.x - b.x);

    // Apply minimum spacing between adjacent nodes
    const MIN_NODE_SPACING = 20;
    for (let i = 1; i < genNodes.length; i++) {
      const prev = genNodes[i - 1];
      const curr = genNodes[i];

      // Type guards to ensure prev and curr exist
      if (!prev || !curr) continue;

      const prevRight = prev.x + NODE_WIDTH;
      const minX = prevRight + MIN_NODE_SPACING;

      if (curr.x < minX) {
        // Shift current node and all subsequent nodes
        const shift = minX - curr.x;
        for (let j = i; j < genNodes.length; j++) {
          const nodeToShift = genNodes[j];
          if (nodeToShift) {
            nodeToShift.x += shift;
          }
        }
      }
    }
  });

  return nodes;
}

// ==================== UNION-BASED CONNECTIONS ====================

/**
 * Derive relationship subtype for edge styling (Phase 2 - Slice 3)
 *
 * Determines if a parent-child relationship is biological, adoptive, or foster
 * based on the child's parentage fields (father_id, mother_id, parents_id).
 *
 * @param childPerson - The child person node
 * @param parentId - The parent's person ID
 * @param union - The union this relationship is part of (if any)
 * @returns Relationship subtype for edge styling
 */
function deriveRelationshipSubtype(
  childPerson: PersonNodeCanvas,
  parentId: string,
  union?: UnionNode
): 'biological' | 'adoptive' | 'foster' | null {
  // Check if this parent is biological (father_id or mother_id matches)
  if (childPerson.father_id === parentId || childPerson.mother_id === parentId) {
    return 'biological';
  }

  // Check if child has adoptive parents (parents_id is set)
  if (childPerson.parents_id) {
    // If the union has a couple_id that matches parents_id, it's adoptive
    if (union && union.couple_id === childPerson.parents_id) {
      return 'adoptive';
    }

    // Otherwise, if parents_id is set but doesn't match this union, assume adoptive
    // This handles cases where we don't have the couple_id but parents_id exists
    if (childPerson.parents_id) {
      return 'adoptive';
    }
  }

  // Default: no subtype determined (renders as solid edge - backward compatible)
  return null;
}

/**
 * Generate orthogonal path from point A to point B using only horizontal and vertical segments
 *
 * Phase 3 Slice 7: Orthogonal routing for cleaner connection lines
 *
 * This function creates SVG paths with 90-degree angles only (no diagonals), which:
 * - Reduces visual clutter in dense trees
 * - Makes parent-child relationships easier to trace
 * - Avoids diagonal lines crossing over other elements
 *
 * Strategy:
 * - Vertical-first approach: drop vertically first, then route horizontally
 * - Uses midpoints to create clean transitions
 * - Falls back to direct vertical line if no horizontal offset exists
 *
 * @param fromX - Starting X coordinate
 * @param fromY - Starting Y coordinate
 * @param toX - Ending X coordinate
 * @param toY - Ending Y coordinate
 * @returns SVG path string using H (horizontal) and V (vertical) commands only
 */
function generateOrthogonalPath(
  fromX: number,
  fromY: number,
  toX: number,
  toY: number
): string {
  const segments: string[] = [];

  // If child is directly below junction (no horizontal offset)
  if (Math.abs(toX - fromX) <= 1) {
    // Simple vertical line
    segments.push(`V ${toY}`);
    return segments.join(' ');
  }

  // Child is offset horizontally - use orthogonal routing
  // Strategy: Vertical first (drop down), then horizontal, then vertical again
  const midY = (fromY + toY) / 2;

  segments.push(`V ${midY}`);      // Vertical to midpoint
  segments.push(`H ${toX}`);       // Horizontal to target X
  segments.push(`V ${toY}`);       // Vertical to target Y

  return segments.join(' ');
}

/**
 * Generate connection lines using union-based logic:
 * - Spouse horizontal lines
 * - Union → Child T-junction pattern
 * - Single-parent L-shaped connections
 */
function generateUnionBasedConnections(
  personNodes: PositionedNode[],
  unions: UnionNode[],
  relationships: RelationshipEdge[]
): ConnectionLine[] {
  const connections: ConnectionLine[] = [];
  const nodeMap = new Map<string, PositionedNode>();

  personNodes.forEach(node => nodeMap.set(node.person.id, node));

  // 1. Spouse connections (horizontal lines between spouses)
  unions.forEach(union => {
    const spouse1 = nodeMap.get(union.spouse1Id);
    const spouse2 = nodeMap.get(union.spouse2Id);

    if (spouse1 && spouse2) {
      const leftSpouse = spouse1.x < spouse2.x ? spouse1 : spouse2;
      const rightSpouse = spouse1.x < spouse2.x ? spouse2 : spouse1;

      connections.push({
        id: `spouse-${union.id}`,
        type: 'spouse',
        fromX: leftSpouse.x + NODE_WIDTH,
        fromY: leftSpouse.y + NODE_HEIGHT / 2,
        toX: rightSpouse.x,
        toY: rightSpouse.y + NODE_HEIGHT / 2,
      });
    }
  });

  // 2. Union → Child connections (T-junction pattern with orthogonal routing)
  // Phase 3 Slice 7: Enhanced with orthogonal routing for cleaner appearance
  unions.forEach(union => {
    const spouse1 = nodeMap.get(union.spouse1Id);
    const spouse2 = nodeMap.get(union.spouse2Id);

    if (!spouse1 || !spouse2) return;

    const leftSpouseX = Math.min(spouse1.x, spouse2.x);
    const rightSpouseX = Math.max(spouse1.x, spouse2.x);
    const unionBottomY = Math.max(spouse1.y, spouse2.y) + NODE_HEIGHT;
    const junctionY = unionBottomY + T_JUNCTION_DROP;

    // Draw T-junction for each child
    union.childIds.forEach(childId => {
      const child = nodeMap.get(childId);
      if (!child) return;

      const childCenterX = child.x + NODE_WIDTH / 2;
      const childTopY = child.y;

      // Build SVG path: horizontal bar → vertical drop → orthogonal path to child
      const pathSegments = [
        `M ${leftSpouseX + NODE_WIDTH},${unionBottomY}`, // Horizontal bar start (left spouse right edge)
        `H ${rightSpouseX}`,                             // Horizontal bar to right spouse left edge
        `M ${union.centerX},${unionBottomY}`,            // Move to union center (below couple)
        `V ${junctionY}`,                                 // Vertical drop to junction point
      ];

      // Phase 3 Slice 7: Orthogonal routing from junction to child
      // Replaces diagonal/L-shaped path with clean 90-degree angles
      const orthogonalSegments = generateOrthogonalPath(
        union.centerX,  // From junction X
        junctionY,      // From junction Y
        childCenterX,   // To child center X
        childTopY       // To child top Y
      );

      pathSegments.push(orthogonalSegments);

      // Derive relationship subtype for edge styling (Phase 2 - Slice 3)
      const relationshipSubtype = deriveRelationshipSubtype(
        child.person,
        union.spouse1Id, // Use either parent to check (both are in same union)
        union
      );

      connections.push({
        id: `union-child-${union.id}-${childId}`,
        type: 'parent-child',
        pathData: pathSegments.join(' '),
        junctionX: childCenterX,
        junctionY: junctionY,
        relationship_subtype: relationshipSubtype, // Phase 2 extension
      });
    });
  });

  // 3. Single-parent connections (for children without a union)
  // Phase 3 Slice 7: Enhanced with orthogonal routing
  const childrenInUnions = new Set<string>();
  unions.forEach(union => {
    union.childIds.forEach(childId => childrenInUnions.add(childId));
  });

  relationships.forEach(rel => {
    if (rel.relationship_type === 'parent_child') {
      const childId = rel.person_b_id;

      // Skip if child is already in a union
      if (childrenInUnions.has(childId)) return;

      const parent = nodeMap.get(rel.person_a_id);
      const child = nodeMap.get(childId);

      if (parent && child) {
        const parentCenterX = parent.x + NODE_WIDTH / 2;
        const parentBottomY = parent.y + NODE_HEIGHT;
        const childCenterX = child.x + NODE_WIDTH / 2;
        const childTopY = child.y;
        const midY = (parentBottomY + childTopY) / 2;

        // Phase 3 Slice 7: Orthogonal routing for single-parent connections
        // Replaces L-shaped path with cleaner orthogonal segments
        const pathSegments = [
          `M ${parentCenterX},${parentBottomY}`, // Start at parent bottom center
        ];

        // Use orthogonal routing from parent to child
        const orthogonalSegments = generateOrthogonalPath(
          parentCenterX,  // From parent center X
          parentBottomY,  // From parent bottom Y
          childCenterX,   // To child center X
          childTopY       // To child top Y
        );

        pathSegments.push(orthogonalSegments);

        // Derive relationship subtype for edge styling (Phase 2 - Slice 3)
        const relationshipSubtype = deriveRelationshipSubtype(
          child.person,
          parent.person.id,
          undefined // No union for single-parent relationships
        );

        connections.push({
          id: `pc-${rel.id}`,
          type: 'parent-child',
          pathData: pathSegments.join(' '),
          junctionX: parentCenterX,
          junctionY: midY,
          relationship_subtype: relationshipSubtype, // Phase 2 extension
        });

        childrenInUnions.add(childId); // Mark as processed
      }
    }
  });

  return connections;
}

// ==================== UTILITIES ====================

/**
 * Determine branch classification for a person
 */
function determineBranch(
  person: PersonNodeCanvas,
  relationships: RelationshipEdge[],
  rootId: string | null
): string {
  if (!rootId) return 'unknown';

  if (person.id === rootId) return 'focal';

  // Check if spouse of root person
  const isSpouse = relationships.some(
    rel =>
      rel.relationship_type === 'spouse' &&
      ((rel.person_a_id === rootId && rel.person_b_id === person.id) ||
        (rel.person_b_id === rootId && rel.person_a_id === person.id))
  );

  if (isSpouse) return 'spouse';

  // Check if child of root person
  const isChild = relationships.some(
    rel =>
      rel.relationship_type === 'parent_child' &&
      rel.person_a_id === rootId &&
      rel.person_b_id === person.id
  );

  if (isChild) return 'child';

  // Check if parent of root person
  const isParent = relationships.some(
    rel =>
      rel.relationship_type === 'parent_child' &&
      rel.person_b_id === rootId &&
      rel.person_a_id === person.id
  );

  if (isParent) {
    return person.gender === 'male' ? 'paternal' : 'maternal';
  }

  return 'unknown';
}

/**
 * Calculate the horizontal offset needed to center the tree
 */
function calculateCenterOffset(nodes: PositionedNode[]): number {
  if (nodes.length === 0) return 0;

  // Find X bounds
  const allX = nodes.map(n => n.x);
  const minX = Math.min(...allX);
  const maxX = Math.max(...allX.map((x, i) => x + NODE_WIDTH));
  const currentTreeCenterX = (minX + maxX) / 2;

  // Calculate offset to center the tree around origin
  return -currentTreeCenterX;
}

/**
 * Center the tree around origin (0, 0)
 * @deprecated Use calculateCenterOffset instead to ensure consistent coordinate space
 */
function centerTree(nodes: PositionedNode[]): PositionedNode[] {
  if (nodes.length === 0) return nodes;

  // Find X bounds
  const allX = nodes.map(n => n.x);
  const minX = Math.min(...allX);
  const maxX = Math.max(...allX.map((x, i) => x + NODE_WIDTH));
  const currentTreeCenterX = (minX + maxX) / 2;

  // Shift all nodes to center the tree around origin
  const offsetX = -currentTreeCenterX;

  const centeredNodes = nodes.map(node => ({
    ...node,
    x: node.x + offsetX,
  }));

  return centeredNodes;
}

/**
 * Calculate bounding box for the entire tree
 */
function calculateBounds(nodes: PositionedNode[]): {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  width: number;
  height: number;
} {
  if (nodes.length === 0) {
    return { minX: 0, maxX: 0, minY: 0, maxY: 0, width: 0, height: 0 };
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  nodes.forEach(node => {
    minX = Math.min(minX, node.x);
    maxX = Math.max(maxX, node.x + NODE_WIDTH);
    minY = Math.min(minY, node.y);
    maxY = Math.max(maxY, node.y + NODE_HEIGHT);
  });

  // Add padding
  minX -= PADDING;
  maxX += PADDING;
  minY -= PADDING;
  maxY += PADDING;

  return {
    minX,
    maxX,
    minY,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

/**
 * Return empty layout
 */
function emptyLayout(): TreeLayout {
  return {
    nodes: [],
    connections: [],
    unions: [], // Phase 3 Slice 4: Empty unions array
    bounds: { minX: 0, maxX: 0, minY: 0, maxY: 0, width: 0, height: 0 },
  };
}

/**
 * Count unions by generation (for logging)
 */
function countByGeneration(unions: UnionNode[]): Record<number, number> {
  const counts: Record<number, number> = {};
  unions.forEach(union => {
    counts[union.generation] = (counts[union.generation] || 0) + 1;
  });
  return counts;
}

// ==================== ROOT DETECTION ====================

/**
 * Determine the root person for a unified family tree
 * Strategy: Find the most "central" person - someone with both parents AND spouse
 * This ensures we show the complete family structure including both lineages
 */
export function determineTreeRoot(
  persons: PersonNodeCanvas[],
  relationships: RelationshipEdge[],
  treeOwnerId?: string
): string | null {
  if (persons.length === 0) return null;

  // Strategy: Find person with BOTH parents AND a spouse
  // This is typically the "middle generation" that connects both families
  const centralPersons = persons.filter(person => {
    const hasParents = relationships.some(
      rel => rel.relationship_type === 'parent_child' && rel.person_b_id === person.id
    );
    const hasSpouse = relationships.some(
      rel => rel.relationship_type === 'spouse' &&
        (rel.person_a_id === person.id || rel.person_b_id === person.id)
    );
    return hasParents && hasSpouse;
  });

  // Prefer tree owner if they're a central person
  if (treeOwnerId && centralPersons.some(p => p.id === treeOwnerId)) {
    return treeOwnerId;
  }

  // Otherwise pick first central person
  if (centralPersons.length > 0) {
    console.log('[TreeRoot] Selected central person:', centralPersons[0]?.first_name, centralPersons[0]?.last_name);
    return centralPersons[0]?.id || null;
  }

  // Fallback 1: Find person with parents but no spouse
  const personsWithParents = persons.filter(person =>
    relationships.some(
      rel => rel.relationship_type === 'parent_child' && rel.person_b_id === person.id
    )
  );

  if (personsWithParents.length > 0) {
    return personsWithParents[0]?.id || null;
  }

  // Fallback 2: Find person with no parents (oldest generation)
  const personsWithoutParents = persons.filter(person => {
    const hasParents = relationships.some(
      rel => rel.relationship_type === 'parent_child' && rel.person_b_id === person.id
    );
    return !hasParents;
  });

  if (personsWithoutParents.length > 0) {
    return personsWithoutParents[0]?.id || null;
  }

  // Final fallback: use first person
  return persons[0]?.id || null;
}
